import { useEffect } from 'react';
import { useParams, Outlet } from 'react-router';

import { TenantRedirect } from '../components/tenant-redirect';

import { Skeleton } from '@/components/ui/skeleton';
import { useTenantLookup } from '@/context/tenant-registry-context';
import { errorIds, logError } from '@/lib/log-error';

/**
 * Route guard for the tenant in the URL — the workspace step of the router
 * gating chain, which is declared once in `GATING_CHAIN` (`router/index.tsx`).
 *
 * The token claim is not allowed to declare an organization non-existent: it
 * lags behind the database, so a brand new organization is missing from it until
 * the claims are re-minted. The guard therefore only redirects on an absence the
 * regional APIs have confirmed, and never on one it merely failed to disprove
 * (ESCLOUD-268).
 *
 * This is a navigation decision, not an authorization one. Membership and role
 * are resolved from the database by the API on every single request; nothing
 * here can grant access, which is what makes failing open safe.
 */
export function TenantGuard() {
    const { tenantId } = useParams<{ tenantId: string }>();
    const lookup = useTenantLookup(tenantId);

    const unprovable = lookup.status === 'unprovable';

    useEffect(() => {
        if (unprovable) {
            logError(
                errorIds.TENANT_CONFIRMATION_UNAVAILABLE,
                'Could not confirm the organization in the URL — a region did not answer. ' +
                    'Rendering the route; the API remains the authority on membership.',
                { tenantId }
            );
        }
    }, [tenantId, unprovable]);

    switch (lookup.status) {
        case 'confirmed':
            return <Outlet />;

        // Cannot prove it is gone, so do not act as if it were: let the route
        // render and let the API answer for each request.
        case 'unprovable':
            return <Outlet />;

        case 'absent':
            return <TenantRedirect />;

        // Still asking. Redirecting here is what discarded newly created
        // organizations.
        default:
            return <TenantGuardSkeleton />;
    }
}

function TenantGuardSkeleton() {
    return (
        <div className="space-y-6 p-6">
            <div>
                <Skeleton className="h-8 w-48" />
                <Skeleton className="mt-2 h-4 w-64" />
            </div>
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
                {[1, 2, 3, 4].map((i) => (
                    <Skeleton key={i} className="h-32 w-full" />
                ))}
            </div>
        </div>
    );
}
